import React, { useState, useEffect } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogClose } from '../../components/ui/dialog';
import { Switch } from '../../components/ui/switch';
import { 
  Users, Plus, Loader2, Shield, Edit, Trash2, Key, 
  UserCheck, UserX, Eye, Briefcase, Crown
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../lib/api';
import { formatDate } from '../../lib/utils';

const UsersPage = () => {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [newUser, setNewUser] = useState({
    email: '',
    full_name: '',
    password: '',
    role: 'recruiter'
  });

  const [editData, setEditData] = useState({
    full_name: '',
    role: '',
    is_active: true
  });

  const [newPassword, setNewPassword] = useState('');

  useEffect(() => {
    fetchUsers();
    fetchRoles();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/users');
      const data = await response.json();
      setUsers(data.users || []);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      toast.error('Erreur lors du chargement des utilisateurs');
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await api.get('/admin/users/roles');
      const data = await response.json();
      setRoles(data.roles || []);
    } catch (error) {
      console.error('Failed to fetch roles:', error);
    }
  };

  const handleCreateUser = async () => {
    if (!newUser.email || !newUser.full_name || !newUser.password) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }
    
    setSaving(true);
    try {
      const response = await api.post('/admin/users', newUser);
      const data = await response.json();
      
      if (response.ok) {
        toast.success(data.message || 'Utilisateur créé');
        setShowCreateDialog(false);
        setNewUser({ email: '', full_name: '', password: '', role: 'recruiter' });
        fetchUsers();
      } else {
        toast.error(data.detail || 'Erreur lors de la création');
      }
    } catch (error) {
      toast.error('Erreur lors de la création');
    } finally {
      setSaving(false);
    }
  };

  const handleEditUser = async () => {
    if (!selectedUser) return;
    
    setSaving(true);
    try {
      const response = await api.patch(`/admin/users/${selectedUser.id}`, editData);
      const data = await response.json();
      
      if (response.ok) {
        toast.success(data.message || 'Utilisateur mis à jour');
        setShowEditDialog(false);
        fetchUsers();
      } else {
        toast.error(data.detail || 'Erreur lors de la mise à jour');
      }
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (!selectedUser || !newPassword) return;
    
    if (newPassword.length < 6) {
      toast.error('Le mot de passe doit avoir au moins 6 caractères');
      return;
    }
    
    setSaving(true);
    try {
      const response = await api.patch(`/admin/users/${selectedUser.id}/password`, {
        new_password: newPassword
      });
      const data = await response.json();
      
      if (response.ok) {
        toast.success(data.message || 'Mot de passe modifié');
        setShowPasswordDialog(false);
        setNewPassword('');
      } else {
        toast.error(data.detail || 'Erreur lors du changement');
      }
    } catch (error) {
      toast.error('Erreur lors du changement de mot de passe');
    } finally {
      setSaving(false);
    }
  };

  const openDeleteDialog = (user) => {
    setSelectedUser(user);
    setDeleteConfirmText('');
    setShowDeleteDialog(true);
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    if (deleteConfirmText !== 'SUPPRIMER') {
      toast.error('Veuillez taper SUPPRIMER pour confirmer');
      return;
    }
    
    setSaving(true);
    try {
      const response = await api.delete(`/admin/users/${selectedUser.id}`);
      const data = await response.json();
      
      if (response.ok) {
        toast.success(data.message || 'Utilisateur supprimé');
        setShowDeleteDialog(false);
        setSelectedUser(null);
        setDeleteConfirmText('');
        fetchUsers();
      } else {
        toast.error(data.detail || 'Erreur lors de la suppression');
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    } finally {
      setSaving(false);
    }
  };

  const openEditDialog = (user) => {
    setSelectedUser(user);
    setEditData({
      full_name: user.full_name,
      role: user.role,
      is_active: user.is_active
    });
    setShowEditDialog(true);
  };

  const openPasswordDialog = (user) => {
    setSelectedUser(user);
    setNewPassword('');
    setShowPasswordDialog(true);
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'super_admin': return <Crown className="h-4 w-4" />;
      case 'admin': return <Shield className="h-4 w-4" />;
      case 'manager': return <Briefcase className="h-4 w-4" />;
      case 'recruiter': return <Users className="h-4 w-4" />;
      case 'viewer': return <Eye className="h-4 w-4" />;
      default: return <Users className="h-4 w-4" />;
    }
  };

  const getRoleBadgeClass = (role) => {
    switch (role) {
      case 'super_admin': return 'bg-purple-100 text-purple-700';
      case 'admin': return 'bg-tp-primary/10 text-tp-primary';
      case 'manager': return 'bg-tp-accent/10 text-tp-accent';
      case 'recruiter': return 'bg-blue-100 text-blue-700';
      case 'viewer': return 'bg-gray-100 text-gray-600';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  return (
    <div className="p-4 md:p-8" data-testid="users-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-display font-semibold mb-2 text-tp-text">Utilisateurs</h1>
          <p className="text-tp-text-light">Gérez les accès au backoffice</p>
        </div>
        
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild>
            <Button className="bg-tp-primary hover:bg-tp-primary-light" data-testid="create-user-button">
              <Plus className="h-4 w-4 mr-2" />
              Nouvel utilisateur
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Créer un utilisateur</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Nom complet *</Label>
                <Input
                  value={newUser.full_name}
                  onChange={(e) => setNewUser({ ...newUser, full_name: e.target.value })}
                  placeholder="Jean Dupont"
                  data-testid="input-new-user-name"
                />
              </div>
              <div>
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  placeholder="jean@example.com"
                  data-testid="input-new-user-email"
                />
              </div>
              <div>
                <Label>Mot de passe *</Label>
                <Input
                  type="password"
                  value={newUser.password}
                  onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                  placeholder="Minimum 6 caractères"
                  data-testid="input-new-user-password"
                />
              </div>
              <div>
                <Label>Rôle *</Label>
                <Select value={newUser.role} onValueChange={(v) => setNewUser({ ...newUser, role: v })}>
                  <SelectTrigger data-testid="select-new-user-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {roles.map(role => (
                      <SelectItem key={role.id} value={role.id}>
                        <div className="flex items-center gap-2">
                          {getRoleIcon(role.id)}
                          <span>{role.name}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-tp-text-light mt-1">
                  {roles.find(r => r.id === newUser.role)?.description}
                </p>
              </div>
            </div>
            <DialogFooter>
              <DialogClose asChild>
                <Button variant="outline">Annuler</Button>
              </DialogClose>
              <Button 
                onClick={handleCreateUser} 
                disabled={saving}
                className="bg-tp-primary hover:bg-tp-primary-light"
                data-testid="confirm-create-user"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Créer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Users Table */}
      <Card className="border-tp-border">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-tp-text-light" />
          </div>
        ) : users.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-tp-text">Utilisateur</TableHead>
                <TableHead className="text-tp-text">Rôle</TableHead>
                <TableHead className="text-tp-text">Statut</TableHead>
                <TableHead className="text-tp-text">Créé le</TableHead>
                <TableHead className="text-tp-text">Dernière connexion</TableHead>
                <TableHead className="text-tp-text text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.id} data-testid={`user-row-${user.id}`}>
                  <TableCell>
                    <div>
                      <p className="font-medium text-tp-text">{user.full_name}</p>
                      <p className="text-xs text-tp-text-light">{user.email}</p>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge className={getRoleBadgeClass(user.role)}>
                      <span className="flex items-center gap-1">
                        {getRoleIcon(user.role)}
                        {user.role_label || user.role}
                      </span>
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {user.is_active ? (
                      <Badge className="bg-tp-success/10 text-tp-success">
                        <UserCheck className="h-3 w-3 mr-1" />
                        Actif
                      </Badge>
                    ) : (
                      <Badge className="bg-tp-error/10 text-tp-error">
                        <UserX className="h-3 w-3 mr-1" />
                        Inactif
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-sm text-tp-text-light">
                    {formatDate(user.created_at)}
                  </TableCell>
                  <TableCell className="text-sm text-tp-text-light">
                    {user.last_login ? formatDate(user.last_login) : '-'}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEditDialog(user)}
                        className="text-tp-text-light hover:text-tp-primary"
                        data-testid={`edit-user-${user.id}`}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openPasswordDialog(user)}
                        className="text-tp-text-light hover:text-tp-primary"
                        data-testid={`password-user-${user.id}`}
                      >
                        <Key className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openDeleteDialog(user)}
                        className="text-tp-text-light hover:text-tp-error"
                        data-testid={`delete-user-${user.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <div className="text-center py-12">
            <Users className="h-12 w-12 text-tp-text-light mx-auto mb-4" />
            <p className="text-tp-text-light">Aucun utilisateur</p>
          </div>
        )}
      </Card>

      {/* Edit User Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Modifier l'utilisateur</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Email</Label>
              <Input value={selectedUser?.email || ''} disabled className="bg-tp-bg" />
            </div>
            <div>
              <Label>Nom complet</Label>
              <Input
                value={editData.full_name}
                onChange={(e) => setEditData({ ...editData, full_name: e.target.value })}
              />
            </div>
            <div>
              <Label>Rôle</Label>
              <Select value={editData.role} onValueChange={(v) => setEditData({ ...editData, role: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {roles.map(role => (
                    <SelectItem key={role.id} value={role.id}>
                      <div className="flex items-center gap-2">
                        {getRoleIcon(role.id)}
                        <span>{role.name}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between p-4 bg-tp-bg rounded-lg">
              <div>
                <Label>Compte actif</Label>
                <p className="text-xs text-tp-text-light">Désactiver empêche la connexion</p>
              </div>
              <Switch
                checked={editData.is_active}
                onCheckedChange={(v) => setEditData({ ...editData, is_active: v })}
              />
            </div>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Annuler</Button>
            </DialogClose>
            <Button 
              onClick={handleEditUser} 
              disabled={saving}
              className="bg-tp-primary hover:bg-tp-primary-light"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Password Dialog */}
      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Changer le mot de passe</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-tp-text-light">
              Nouveau mot de passe pour <strong>{selectedUser?.full_name}</strong>
            </p>
            <div>
              <Label>Nouveau mot de passe</Label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Minimum 6 caractères"
              />
            </div>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Annuler</Button>
            </DialogClose>
            <Button 
              onClick={handleChangePassword} 
              disabled={saving}
              className="bg-tp-primary hover:bg-tp-primary-light"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Changer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Role Legend */}
      <Card className="mt-6 p-6 border-tp-border">
        <h3 className="text-sm font-semibold text-tp-text mb-4">Légende des rôles</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {roles.map(role => (
            <div key={role.id} className="flex items-start gap-3">
              <Badge className={getRoleBadgeClass(role.id)}>
                {getRoleIcon(role.id)}
              </Badge>
              <div>
                <p className="text-sm font-medium text-tp-text">{role.name}</p>
                <p className="text-xs text-tp-text-light">{role.description}</p>
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Double Confirmation Delete Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-tp-error flex items-center gap-2">
              <Trash2 className="h-5 w-5" />
              Confirmer la suppression
            </DialogTitle>
          </DialogHeader>
          
          {selectedUser && (
            <div className="space-y-4">
              <div className="p-4 bg-tp-error/10 border border-tp-error/20 rounded-lg">
                <p className="text-sm text-tp-text">
                  Vous êtes sur le point de supprimer définitivement l'utilisateur :
                </p>
                <p className="mt-2 font-semibold text-tp-text">
                  {selectedUser.full_name} ({selectedUser.email})
                </p>
                <p className="mt-2 text-sm text-tp-error">
                  Cette action est irréversible.
                </p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="delete-confirm" className="text-sm text-tp-text">
                  Pour confirmer, tapez <span className="font-bold text-tp-error">SUPPRIMER</span> ci-dessous :
                </Label>
                <Input
                  id="delete-confirm"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder="SUPPRIMER"
                  className="font-mono"
                  data-testid="delete-confirm-input"
                />
              </div>
            </div>
          )}
          
          <DialogFooter className="gap-2 sm:gap-0">
            <DialogClose asChild>
              <Button variant="outline" disabled={saving}>
                Annuler
              </Button>
            </DialogClose>
            <Button
              variant="destructive"
              onClick={handleDeleteUser}
              disabled={saving || deleteConfirmText !== 'SUPPRIMER'}
              className="bg-tp-error hover:bg-tp-error/90"
              data-testid="confirm-delete-button"
            >
              {saving ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4 mr-2" />
              )}
              Supprimer définitivement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UsersPage;
